import React, { useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const renderMetadata = (metadata) => {
  if (!metadata || typeof metadata !== "object") {
    return "No additional details configured.";
  }

  try {
    return JSON.stringify(metadata, null, 2);
  } catch (error) {
    return Object.keys(metadata).join(", ");
  }
};

const FallbackMetadata = ({ metadata }) => (
  <Box
    component="pre"
    sx={{
      bgcolor: "grey.100",
      borderRadius: 1,
      p: 2,
      fontFamily: "inherit",
      fontSize: "0.875rem",
      whiteSpace: "pre-wrap",
      overflowX: "auto",
    }}
  >
    {renderMetadata(metadata)}
  </Box>
);

const TableSectionPreview = ({ metadata }) => {
  const columns = Array.isArray(metadata?.columns) ? metadata.columns : [];
  const rows = Array.isArray(metadata?.rows) ? metadata.rows : [];
  const dropdowns = Array.isArray(metadata?.dropdowns) ? metadata.dropdowns : [];
  const description =
    typeof metadata?.description === "string"
      ? metadata.description.trim()
      : "";

  const dropdownMap = useMemo(
    () => new Map(dropdowns.map((dropdown) => [dropdown.id, dropdown])),
    [dropdowns]
  );

  if (!columns.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Configure at least one column to preview the table.
      </Typography>
    );
  }

  const renderCellContent = (cell, column) => {
    const direction = column?.direction || "rtl";
    const allowMultiline = column?.multiline !== false;
    const textAlign = direction === "ltr" ? "left" : "right";
    const baseBoxSx = {
      minHeight: 56,
      px: 1.5,
      py: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: textAlign === "left" ? "flex-start" : "flex-end",
      gap: 0.25,
      width: "100%",
      direction,
      textAlign,
      whiteSpace: allowMultiline ? "pre-wrap" : "normal",
      wordBreak: "break-word",
    };

    const renderPlaceholder = (content, placeholder = "Student response") => (
      <Box sx={baseBoxSx}>
        <Typography
          variant="body2"
          color={content ? "text.primary" : "text.secondary"}
        >
          {content || placeholder}
        </Typography>
      </Box>
    );

    if (!cell || typeof cell !== "object") {
      const fallback = typeof cell === "string" ? cell : "";
      return renderPlaceholder(fallback);
    }

    switch (cell.type) {
      case "readonly":
      case "question":
        return (
          <Box sx={baseBoxSx}>
            <Typography variant="body2">
              {cell.text || cell.value || ""}
            </Typography>
          </Box>
        );
      case "dropdown": {
        const dropdown = dropdownMap.get(cell.dropdownId);
        const options = Array.isArray(dropdown?.options)
          ? dropdown.options
          : [];
        const allowMultiple = Boolean(dropdown?.allowMultiple);
        const placeholder = allowMultiple
          ? "Select option(s)"
          : "Select an option";

        return (
          <Box sx={baseBoxSx}>
            <Typography variant="body2" color="text.secondary">
              {placeholder}
            </Typography>
            {options.length > 0 && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  alignSelf: textAlign === "left" ? "flex-start" : "flex-end",
                }}
              >
                Options:
                {" "}
                {options
                  .map((option, optionIndex) =>
                    option?.label || `Option ${optionIndex + 1}`
                  )
                  .join(", ")}
              </Typography>
            )}
          </Box>
        );
      }
      case "input":
      default:
        return renderPlaceholder(cell.prefill);
    }
  };

  return (
    <Stack spacing={2}>
      {description && (
        <Paper
          variant="outlined"
          sx={{ p: 2, bgcolor: "background.default", boxShadow: "none" }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Section description
          </Typography>
          <Box
            sx={{
              typography: "body2",
              color: "text.secondary",
              "& img": { maxWidth: "100%", height: "auto" },
              "& iframe": { maxWidth: "100%" },
            }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </Paper>
      )}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ boxShadow: "none" }}
      >
        <Table
          size="small"
          sx={{
            border: "1px solid",
            borderColor: "divider",
            "& th, & td": {
              border: "1px solid",
              borderColor: "divider",
            },
            "& thead th": {
              bgcolor: "grey.100",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id || column.accessor || index}
                  sx={{
                    direction: column?.direction || "rtl",
                    textAlign:
                      column?.direction === "ltr" ? "left" : "right",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {column.header || column.accessor || `Column ${index + 1}`}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    Add rows to preview how learners will complete the table.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow key={`row-${rowIndex}`}>
                  {columns.map((column, columnIndex) => (
                    <TableCell
                      key={`${rowIndex}-${column.id || column.accessor || columnIndex}`}
                      sx={{
                        direction: column?.direction || "rtl",
                        textAlign:
                          column?.direction === "ltr" ? "left" : "right",
                      }}
                    >
                      {renderCellContent(row?.[columnIndex], column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

const HighlightSectionPreview = ({ metadata }) => {
  const passage = metadata?.passage || "";
  const highlights = Array.isArray(metadata?.highlights)
    ? metadata.highlights
    : [];
  const accessibilityPreview = Boolean(metadata?.accessibilityPreview);

  const validHighlights = highlights
    .map((highlight) => {
      const start = Number(highlight?.start);
      const end = Number(highlight?.end);

      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return null;
      }

      const safeStart = Math.max(0, Math.min(passage.length, start));
      const safeEnd = Math.max(safeStart, Math.min(passage.length, end));

      if (safeEnd <= safeStart) {
        return null;
      }

      return {
        ...highlight,
        start: safeStart,
        end: safeEnd,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  if (!passage.trim()) {
    return (
      <Typography variant="body2" color="text.secondary">
        Add a passage to preview the highlight experience.
      </Typography>
    );
  }

  const segments = [];
  let lastIndex = 0;

  validHighlights.forEach((highlight) => {
    if (highlight.start > lastIndex) {
      segments.push({
        text: passage.slice(lastIndex, highlight.start),
        isHighlight: false,
      });
    }

    segments.push({
      text: passage.slice(highlight.start, highlight.end),
      isHighlight: true,
      label: highlight.label,
    });

    lastIndex = highlight.end;
  });

  if (lastIndex < passage.length) {
    segments.push({
      text: passage.slice(lastIndex),
      isHighlight: false,
    });
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
        <Typography component="div" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {segments.length === 0
            ? passage
            : segments.map((segment, index) =>
                segment.isHighlight ? (
                  <Box
                    key={`highlight-${index}`}
                    component="mark"
                    sx={{
                      bgcolor: "primary.light",
                      color: "inherit",
                      px: 0.5,
                      borderRadius: 0.5,
                      display: "inline",
                    }}
                  >
                    {segment.text}
                  </Box>
                ) : (
                  <React.Fragment key={`segment-${index}`}>
                    {segment.text}
                  </React.Fragment>
                )
              )}
        </Typography>
      </Paper>

      {accessibilityPreview && validHighlights.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Accessible highlight summary</Typography>
            {validHighlights.map((highlight, index) => (
              <Box key={`summary-${index}`}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {highlight.label || `Highlight ${index + 1}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {passage.slice(highlight.start, highlight.end)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

const SectionPreviewContent = ({ section }) => {
  const template = section?.template || section?.type;

  switch (template) {
    case "table":
      return <TableSectionPreview metadata={section?.metadata} />;
    case "highlight":
      return <HighlightSectionPreview metadata={section?.metadata} />;
    default:
      return <FallbackMetadata metadata={section?.metadata} />;
  }
};

const SectionPreview = ({ section }) => (
  <Stack spacing={2}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
        {section.title || "Untitled section"}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {section.template
          ? `Template: ${section.template}`
          : "Custom section"}
      </Typography>
    </Box>
    <Divider />
    <SectionPreviewContent section={section} />
  </Stack>
);

const AssignmentPreview = ({ sections }) => {
  if (!sections || sections.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Add sections to preview the assignment content.
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      {sections.map((section, index) => (
        <Stack key={section.id} spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {`Section ${index + 1}: ${section.title || "Untitled section"}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {section.template
              ? `Template: ${section.template}`
              : "Custom section"}
          </Typography>
          <SectionPreviewContent section={section} />
          {index < sections.length - 1 && <Divider />}
        </Stack>
      ))}
    </Stack>
  );
};

const AssignmentPreviewDialog = ({
  open,
  onClose,
  sections,
  sectionId,
  showAssignmentPreview,
}) => {
  const section = useMemo(
    () => sections?.find((candidate) => candidate.id === sectionId) || null,
    [sections, sectionId]
  );

  const isSectionPreview = Boolean(section) && !showAssignmentPreview;
  const title = isSectionPreview
    ? `Preview: ${section?.title || "Untitled section"}`
    : "Assignment preview";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {isSectionPreview ? (
          <SectionPreview section={section} />
        ) : (
          <AssignmentPreview sections={sections} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentPreviewDialog;
